import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './ExternalLawyers.css';

const ExternalLawyers = ({ caseData, onBack, token }) => {
  const [assignedLawyers, setAssignedLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLawyerModal, setShowAddLawyerModal] = useState(false);
  const [allLawyers, setAllLawyers] = useState([]);
  const [lawyerSearchTerm, setLawyerSearchTerm] = useState('');
  const [loadingLawyers, setLoadingLawyers] = useState(false);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [assigningLawyer, setAssigningLawyer] = useState(false);
  const [deletingLawyer, setDeletingLawyer] = useState(null);

  // Fetch assigned lawyers for this case
  const fetchAssignedLawyers = async () => {
    try {
      setLoading(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/get/cases/assign-lawyers/${caseData.id}`;
      console.log('Fetching assigned lawyers from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Assigned lawyers response:', data);

      if (response.ok && data.data) {
        // The API returns case data with lawyers array inside
        const lawyers = data.data.lawyers || [];
        setAssignedLawyers(Array.isArray(lawyers) ? lawyers : []);
      } else {
        setAssignedLawyers([]);
      }
    } catch (err) {
      console.error('Error fetching assigned lawyers:', err);
      setAssignedLawyers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all lawyers for the modal
  const fetchAllLawyers = async () => {
    try {
      setLoadingLawyers(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/guests/getLawyers`;
      console.log('Fetching all lawyers from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('All lawyers response:', data);

      if (response.ok && data.data && Array.isArray(data.data)) {
        setAllLawyers(data.data);
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to fetch lawyers',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error fetching lawyers:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to fetch lawyers',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoadingLawyers(false);
    }
  };

  // Assign lawyers to case
  const handleAssignLawyers = async () => {
    if (selectedLawyers.length === 0) {
      Swal.fire({
        title: 'Warning',
        text: 'Please select at least one lawyer',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setAssigningLawyer(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/cases/assign-lawyers/${caseData.id}`;
      console.log('Assigning lawyers to case:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          lawyer_ids: selectedLawyers
        }),
      });

      const data = await response.json();
      console.log('Assign lawyers response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Lawyers assigned successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        setShowAddLawyerModal(false);
        setSelectedLawyers([]);
        setLawyerSearchTerm('');
        fetchAssignedLawyers(); // Refresh the list
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to assign lawyers',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error assigning lawyers:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to assign lawyers',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setAssigningLawyer(false);
    }
  };

  // Remove lawyer from case
  const handleRemoveLawyer = async (lawyerId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to remove this lawyer from the case?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeletingLawyer(lawyerId);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/case/${caseData.id}/lawyer/${lawyerId}`;
      console.log('Removing lawyer from case:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Remove lawyer response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Removed!',
          text: 'Lawyer removed successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        fetchAssignedLawyers(); // Refresh the list
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to remove lawyer',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error removing lawyer:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to remove lawyer',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeletingLawyer(null);
    }
  };

  // Open Add Lawyer modal
  const openAddLawyerModal = () => {
    setShowAddLawyerModal(true);
    setSelectedLawyers([]);
    setLawyerSearchTerm('');
    fetchAllLawyers();
  };

  // Toggle lawyer selection
  const toggleLawyerSelection = (lawyerId) => {
    setSelectedLawyers(prev => {
      if (prev.includes(lawyerId)) {
        return prev.filter(id => id !== lawyerId);
      } else {
        return [...prev, lawyerId];
      }
    });
  };

  useEffect(() => {
    fetchAssignedLawyers();
  }, [caseData.id]);

  return (
    <div className="external-lawyers-container">
      {/* Header */}
      <div className="external-lawyers-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>External Lawyers</h2>
        <button className="add-lawyer-button" onClick={openAddLawyerModal}>
          +
        </button>
      </div>

      {/* Lawyers List */}
      <div className="external-lawyers-content">
        {loading ? (
          <div className="loading-state">
            <p>Loading lawyers...</p>
          </div>
        ) : assignedLawyers.length > 0 ? (
          <div className="assigned-lawyers-list">
            {assignedLawyers.map(lawyer => {
              const lawyerName = lawyer.name || 
                                (lawyer.first_name && lawyer.last_name ? `${lawyer.first_name} ${lawyer.last_name}` : '') ||
                                lawyer.first_name || 
                                lawyer.last_name || 
                                'Unknown';
              const lawyerCity = lawyer.city || '';
              const lawyerState = lawyer.state || '';
              const location = [lawyerCity, lawyerState].filter(Boolean).join(', ') || 'Location not specified';
              const licenseNo = lawyer.license_no || 'N/A';
              const phoneNo = lawyer.mobile_number || lawyer.phone || 'N/A';

              return (
                <div key={lawyer.id} className="lawyer-card">
                  <button
                    className="delete-lawyer-button"
                    onClick={() => handleRemoveLawyer(lawyer.id)}
                    disabled={deletingLawyer === lawyer.id}
                  >
                    {deletingLawyer === lawyer.id ? '⏳' : '🗑️'}
                  </button>

                  <div className="lawyer-card-avatar">
                    {lawyer.profile_image ? (
                      <img 
                        src={`${process.env.REACT_APP_API_URL}/${lawyer.profile_image}`} 
                        alt={lawyerName}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="lawyer-avatar-placeholder" style={{ display: lawyer.profile_image ? 'none' : 'flex' }}>
                      {lawyerName.charAt(0).toUpperCase()}
                    </div>
                  </div>

                  <div className="lawyer-card-info">
                    <h3 className="lawyer-card-name">{lawyerName}</h3>
                    <p className="lawyer-card-location">{location}</p>
                    <p className="lawyer-card-license">License No. {licenseNo}</p>
                    <p className="lawyer-card-phone">Phone No. {phoneNo}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-lawyers-state">
            <div className="no-lawyers-illustration">
              <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="100" cy="100" r="80" fill="#E8E8E8"/>
                <path d="M70 90 Q 100 70, 130 90" stroke="#333" strokeWidth="3" fill="none"/>
                <circle cx="85" cy="95" r="3" fill="#333"/>
                <circle cx="115" cy="95" r="3" fill="#333"/>
                <path d="M85 120 Q 100 130, 115 120" stroke="#333" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            <p className="no-lawyers-text">No lawyer is added to this case.</p>
          </div>
        )}
      </div>

      {/* Add Lawyer Modal */}
      {showAddLawyerModal && (
        <div className="modal-overlay" onClick={() => setShowAddLawyerModal(false)}>
          <div className="modal-content add-lawyer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Lawyer to Case</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddLawyerModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="case-title">Case #{caseData.caseNumber}: {caseData.caseType}</p>

              {/* Search Input */}
              <div className="lawyer-search-container">
                <input
                  type="text"
                  className="lawyer-search-input"
                  placeholder="Search by name or phone number..."
                  value={lawyerSearchTerm}
                  onChange={(e) => setLawyerSearchTerm(e.target.value)}
                />
              </div>

              {/* Lawyers List */}
              <div className="lawyers-list-container">
                {loadingLawyers ? (
                  <div className="loading-state">
                    <p>Loading lawyers...</p>
                  </div>
                ) : (
                  <div className="lawyers-list">
                    {allLawyers
                      .filter(lawyer => {
                        const searchLower = lawyerSearchTerm.toLowerCase();
                        const firstName = (lawyer.first_name || '')?.toLowerCase();
                        const lastName = (lawyer.last_name || '')?.toLowerCase();
                        const fullName = `${firstName} ${lastName}`.trim();
                        const name = (lawyer.name || lawyer.full_name || lawyer.user_name || '')?.toLowerCase();
                        const phone = (lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || '')?.toLowerCase();
                        
                        return firstName.includes(searchLower) || 
                               lastName.includes(searchLower) || 
                               fullName.includes(searchLower) ||
                               name.includes(searchLower) || 
                               phone.includes(searchLower);
                      })
                      .map(lawyer => {
                        const lawyerName = lawyer.name || 
                                          (lawyer.first_name && lawyer.last_name ? `${lawyer.first_name} ${lawyer.last_name}` : '') ||
                                          lawyer.first_name || 
                                          lawyer.last_name || 
                                          lawyer.full_name || 
                                          lawyer.user_name || 
                                          'Unknown';
                        const lawyerPhone = lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || 'No phone';
                        
                        return (
                        <div
                          key={lawyer.id}
                          className={`lawyer-item ${selectedLawyers.includes(lawyer.id) ? 'selected' : ''}`}
                          onClick={() => toggleLawyerSelection(lawyer.id)}
                        >
                          <div className="lawyer-info">
                            <div className="lawyer-avatar">
                              {lawyerName.charAt(0).toUpperCase()}
                            </div>
                            <div className="lawyer-details">
                              <div className="lawyer-name">{lawyerName}</div>
                              <div className="lawyer-phone">{lawyerPhone}</div>
                            </div>
                          </div>
                          <div className="lawyer-checkbox">
                            {selectedLawyers.includes(lawyer.id) ? '✓' : ''}
                          </div>
                        </div>
                        );
                      })}
                    {allLawyers.filter(lawyer => {
                      const searchLower = lawyerSearchTerm.toLowerCase();
                      const firstName = (lawyer.first_name || '')?.toLowerCase();
                      const lastName = (lawyer.last_name || '')?.toLowerCase();
                      const fullName = `${firstName} ${lastName}`.trim();
                      const name = (lawyer.name || lawyer.full_name || lawyer.user_name || '')?.toLowerCase();
                      const phone = (lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || '')?.toLowerCase();
                      
                      return firstName.includes(searchLower) || 
                             lastName.includes(searchLower) || 
                             fullName.includes(searchLower) ||
                             name.includes(searchLower) || 
                             phone.includes(searchLower);
                    }).length === 0 && (
                      <div className="no-lawyers">
                        <p>No lawyers found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedLawyers.length > 0 && (
                <div className="selected-count">
                  {selectedLawyers.length} lawyer{selectedLawyers.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowAddLawyerModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleAssignLawyers}
                disabled={assigningLawyer || selectedLawyers.length === 0}
              >
                {assigningLawyer ? 'Adding...' : `Add ${selectedLawyers.length > 0 ? selectedLawyers.length : ''} Lawyer${selectedLawyers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalLawyers;
